import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionData, SubscriptionPackage } from '../BillingTypes';
import { featureComparison, highlightedFeatures } from '../packageOptions';
import SwitchButton from 'components/buttons/SwitchButton';
import { useSettings } from 'services/settings/SettingsContext';
import { capitalizeString } from 'services/utils/capitalizeText';
import { formatDate } from 'services/utils/dateTimeUtils';
import SeatsSelection from './SeatsSelection';

interface PackageSelectionProps {
    loading: string;
    packages: SubscriptionPackage[];
    activeUsers: { normal: string, field?: string, light?: string };
    currentSubscription: SubscriptionData;
    updatedSubscription: SubscriptionData;
    setUpdatedSubscription: React.Dispatch<React.SetStateAction<SubscriptionData>>;
};

const PackageSelection: React.FC<PackageSelectionProps> = ({ 
    loading, packages, activeUsers, currentSubscription, updatedSubscription, setUpdatedSubscription
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const [showComparisonTable, setShowComparisonTable] = useState<boolean>(false);

    // Find the selected package
    const selectedPackageObject = packages.find(pkg => pkg.key === updatedSubscription.package);

    // Determine wheter its a single seat package
    const isSingleSeatPackage = selectedPackageObject?.seat_limit === 1;

    // Determine if the package has field and/or light licenses
    const hasFieldLicenses = selectedPackageObject?.enable_field_seats ?? false;
    const hasLightLicenses = selectedPackageObject?.enable_light_seats ?? false;

    // When a package is changed without field or light seats, change the amount of seats
    useEffect(() => {
        if (!selectedPackageObject || isSingleSeatPackage) return;

        const fieldSeats = parseInt(updatedSubscription.seats.field || '0', 10);
        const lightSeats = parseInt(updatedSubscription.seats.light || '0', 10);
        const normalSeats = parseInt(updatedSubscription.seats.normal || '0', 10);

        let newNormal = normalSeats;
        let newField = fieldSeats;
        let newLight = lightSeats;

        let shouldUpdate = false;

        // Convert field → normal
        if (!hasFieldLicenses && fieldSeats > 0) {
            newNormal += fieldSeats;
            newField = 0;
            shouldUpdate = true;
        }

        // Convert light → normal
        if (!hasLightLicenses && lightSeats > 0) {
            newNormal += lightSeats;
            newLight = 0;
            shouldUpdate = true;
        }

        if (shouldUpdate) {
            setUpdatedSubscription(prev => ({
                ...prev,
                seats: {
                    normal: String(newNormal),
                    field: String(newField),
                    light: String(newLight)
                }
            }));
        }

    }, [selectedPackageObject, hasFieldLicenses, hasLightLicenses, isSingleSeatPackage]);

    // Raise the entered seats to at least the active users so used licenses are always ordered
    useEffect(() => {
        if (!selectedPackageObject || isSingleSeatPackage) return;

        // Parse the values
        const parse = (val?: string | number) => parseInt(String(val ?? "0"), 10) || 0;

        setUpdatedSubscription(prev => {

            // Get the active users per license type the package supports
            const minimum = {
                normal: parse(activeUsers.normal),
                field: hasFieldLicenses ? parse(activeUsers.field) : 0,
                light: hasLightLicenses ? parse(activeUsers.light) : 0,
            };

            // Get the currently entered seats
            const entered = {
                normal: parse(prev.seats.normal),
                field: parse(prev.seats.field),
                light: parse(prev.seats.light),
            };

            // Keep the entered seats when they already cover the active users
            if (entered.normal >= minimum.normal && entered.field >= minimum.field && entered.light >= minimum.light) {
                return prev;
            }

            // Raise the seat types which are below the amount of active users
            return {
                ...prev,
                seats: {
                    normal: String(Math.max(entered.normal, minimum.normal)),
                    field: String(Math.max(entered.field, minimum.field)),
                    light: String(Math.max(entered.light, minimum.light)),
                }
            };
        });
    }, [selectedPackageObject, isSingleSeatPackage, hasFieldLicenses, hasLightLicenses, activeUsers, setUpdatedSubscription,
        updatedSubscription.seats.normal, updatedSubscription.seats.field, updatedSubscription.seats.light]);

    // Set the seat limit, and optionally hide the manage seat button
    useEffect(() => {
        if (isSingleSeatPackage) {
            setUpdatedSubscription(prev => {
                const alreadyCorrect =
                    prev.seats.normal === "1" &&
                    prev.seats.field === "0" &&
                    prev.seats.light === "0";

                if (alreadyCorrect) return prev;

                return {
                    ...prev,
                    seats: {
                        normal: "1",
                        field: "0",
                        light: "0"
                    }
                };
            });
        }
    }, [isSingleSeatPackage]);

    // Get the selected amount of seats
    const getAmountOfSeats = () => {

        const normalSeats = parseInt(updatedSubscription.seats.normal ?? "0");
        const fieldSeats = parseInt(updatedSubscription.seats.field ?? "0");
        const lightSeats = parseInt(updatedSubscription.seats.light ?? "0");

        const parts = [];

        if (normalSeats > 0) {
            parts.push(`${normalSeats} ${t('billing.general.normal_seats_short_label')}`);
        }

        if (fieldSeats > 0) {
            parts.push(`${fieldSeats} ${t('billing.general.field_seats_short_label')}`);
        }

        if (lightSeats > 0) {
            parts.push(`${lightSeats} ${t('billing.general.light_seats_short_label')}`);
        }

        return `${parts.join(' + ')} ${t('billing.general.seats_label')}`;
    };

    // // Handle open manage seats modal
    // const handleOpenManageSeatsModal = () => {
    //     initializeModal(
    //         <ManageSeatsModal 
    //             activeUsers={activeUsers}
    //             packages={packages}
    //             hasLightLicenses={hasLightLicenses}
    //             selectedPackage={updatedSubscription.package}
    //             selectedDuration={updatedSubscription.duration}
    //             enteredSeats={updatedSubscription.seats}
    //             setEnteredSeats={(seats: { normal: string; field?: string, light?: string }) =>
    //                 setUpdatedSubscription(prev => ({ ...prev, seats: { 
    //                     normal: seats.normal, 
    //                     field: seats.field,
    //                     light: seats.light 
    //                 }}))
    //             }
    //         />, { 
    //             title: t('billing.general.manage_seats_header'),
    //             modalSize: 'small-medium', 
    //         }
    //     )
    // };

    // Determine if the subscription duration changed
    const configurePendingSubscriptionMessage = (currentSubscription: SubscriptionData) => {

        // If no end date or pending change is given, return null
        if (!currentSubscription.end_date || !currentSubscription.pending_subscription) {
            return null;
        }

        // Set the default message for pending downgrades
        let pendingSubscriptionMessage = t('billing.general.pending_downgrade_message', { 
            currentPackage: capitalizeString(currentSubscription.package),
            currentEndDate: formatDate(currentSubscription.end_date, userLocale, 'writtenMonth'),
            pendingPackage: capitalizeString(currentSubscription.pending_subscription.package)
        })

        // If the duration of the pending subscription differs, show the duration in the message
        if (currentSubscription.duration !== 'trial' && currentSubscription.duration !== currentSubscription.pending_subscription?.duration) {
            pendingSubscriptionMessage = t('billing.general.pending_duration_change_message', { 
                currentDuration: currentSubscription.duration === 'monthly' ? t('billing.general.monthly_label') : t('billing.general.yearly_label'),
                currentPackage: capitalizeString(currentSubscription.package),
                currentEndDate: formatDate(currentSubscription.end_date, userLocale, 'writtenMonth'),
                pendingDuration: currentSubscription.pending_subscription.duration === 'monthly' ? t('billing.general.monthly_label') : t('billing.general.yearly_label'),
                pendingPackage: capitalizeString(currentSubscription.pending_subscription.package)
            })
        }

        return pendingSubscriptionMessage;
    };

    // Format the price
    const getFormattedPrice = (price: string) => {
        // Convert the price into a float and check if it is a round number
        const value = parseFloat(price);
        const isRound = Number.isInteger(value);

        // Format the price based on the user locale
        const formatted = value.toLocaleString(userLocale, {
            minimumFractionDigits: isRound ? 0 : 2,
            maximumFractionDigits: 2,
        });

        return isRound ? `${formatted},-` : formatted;
    };

    return (
        <div className='wizard-step'>
            <div className='container-filled header-title'>
                <h2>{t('billing.general.set_subscription_header')}</h2>
            </div>
            {loading === 'show-loader' ? (
                <div className="loader"></div>
            ) : loading === 'success' && (
                <div className='step-content'>

                    {currentSubscription.pending_subscription && currentSubscription.end_date && (
                        <div className='container-filled pending-downgrade-message'>
                            <div className='message-box'>
                                <h6>
                                    {t('billing.general.pending_subscription_title')}
                                </h6>
                                <div>
                                    {configurePendingSubscriptionMessage(currentSubscription)}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='container-filled duration-selector'>
                        <span className={`duration-label left-label ${updatedSubscription.duration === 'monthly' ? 'active' : ''}`} 
                                onClick={() => setUpdatedSubscription(prev => ({ ...prev, duration: 'monthly' }) )}>
                            {t('billing.general.monthly_billing_label')}
                        </span>
                        <SwitchButton
                            value={updatedSubscription.duration === 'yearly' ? true : updatedSubscription.duration === 'trial' ? true : false}
                            defaultValue={'on'}
                            onToggle={(isOn) => setUpdatedSubscription(prev => ({ ...prev, duration: isOn ? 'yearly' : 'monthly' }) )}
                        />
                        <span className={`duration-label right-label ${updatedSubscription.duration === 'yearly' ? 'active' : ''}`}
                                onClick={() => setUpdatedSubscription(prev => ({ ...prev, duration: 'yearly' }) )}>
                            {t('billing.general.yearly_billing_label')}
                        </span>
                    </div>

                    <div className={`package-options ${packages.filter(pkg => pkg.is_active && !pkg.is_view_only).length > 4 ? 'package-container' : 'container-filled'}`}
                         style={{ gridTemplateColumns: `repeat(${packages.filter(pkg => pkg.is_active && !pkg.is_view_only).length}, 1fr)` }}>
                        {packages.filter(pkg => pkg.is_active && !pkg.is_view_only).map((pkg, index) => (
                            <div className={`package-option ${pkg.key === updatedSubscription.package ? 'selected' : ''} ${pkg.is_best_choice && 'best-choice'}`}
                                 key={pkg.key}
                                 onClick={() => setUpdatedSubscription(prev => ({ ...prev, package: pkg.key }) )}>
                                <div className='package-content'>
                                    {pkg.is_best_choice && (
                                        <div className='best-choice-label'>
                                            {t(`billing.general.best_choice_label`)}
                                        </div>
                                    )}
                                    <h4 className='package-label'>
                                        {pkg.name}
                                    </h4>
                                    <div className='package-explanation'>
                                        {pkg.explanation_nl}
                                    </div>
                                    <div className='package-features'>
                                        <div className='included-label'>
                                            {index === 0
                                                ? t(`billing.general.first_included_label`)
                                                : t(`billing.general.included_label`, { packageName: packages.filter(pkg => pkg.is_active && !pkg.is_view_only)[index - 1].name })}
                                        </div>
                                        {pkg.highlighted_features_nl_list?.length
                                            ? pkg.highlighted_features_nl_list.map((highlighted_feature, index) => (
                                                <div className='included-feature' key={index}>
                                                    {highlighted_feature}
                                                </div>
                                            )) : null
                                        }
                                    </div>
                                    <div className='package-price'>
                                        <div className='seat-price'>
                                            <span className='seat-label'>
                                                {t('billing.general.full_user_label')}
                                            </span>
                                            <span className='price-label'>
                                                <span className='currency-icon'>€ </span>
                                                {updatedSubscription.duration === 'yearly' || updatedSubscription.duration === 'trial'
                                                    ? getFormattedPrice(pkg.yearly_price)
                                                    : getFormattedPrice(pkg.monthly_price)
                                                }
                                            </span>
                                        </div>

                                        {pkg.enable_field_seats && (
                                            <div className='seat-price'>
                                                <span className='seat-label'>
                                                    {t('billing.general.field_user_label')}
                                                </span>
                                                <span className='price-label field'>
                                                    <span className='currency-icon'>€ </span>
                                                    {updatedSubscription.duration === 'yearly' || updatedSubscription.duration === 'trial'
                                                        ? getFormattedPrice(pkg.yearly_field_price)
                                                        : getFormattedPrice(pkg.monthly_field_price)
                                                    }
                                                </span>
                                            </div>
                                        )}

                                        <div className='price-unit-label'>
                                            {pkg.seat_limit === 1 ? (
                                                updatedSubscription.duration === 'yearly' 
                                                    ? t('billing.general.yearly_invoiced_label_one_seat') 
                                                    : updatedSubscription.duration === 'trial' ? t('billing.general.yearly_invoiced_label_one_seat') 
                                                    : t('billing.general.monthly_invoiced_label_one_seat')
                                            ) : (
                                                <>
                                                    <span>{t('billing.general.per_seat_per_month_label')}, </span>
                                                    <span>
                                                        {updatedSubscription.duration === 'yearly' 
                                                            ? t('billing.general.yearly_invoiced_label') 
                                                            : updatedSubscription.duration === 'trial' ? t('billing.general.yearly_invoiced_label') 
                                                            : t('billing.general.monthly_invoiced_label')}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {!isSingleSeatPackage && (
                        <SeatsSelection 
                            activeUsers={activeUsers}
                            packages={packages}
                            hasFieldLicenses={hasFieldLicenses}
                            hasLightLicenses={hasLightLicenses}
                            selectedPackage={updatedSubscription.package}
                            selectedDuration={updatedSubscription.duration}
                            enteredSeats={updatedSubscription.seats}
                            setEnteredSeats={(seats: { normal: string; field?: string, light?: string }) =>
                                setUpdatedSubscription(prev => ({ ...prev, seats: { 
                                    normal: seats.normal, 
                                    field: seats.field,
                                    light: seats.light 
                                }}))
                            }
                        />
                        // <div className='container-filled seats-selection'>
                        //     <div className='seats-header'>
                        //         <h3>
                        //             {t('billing.general.seats_amount_label')}
                        //         </h3>
                        //         <p className='seats-description'>
                        //             {t('billing.general.seats_amount_description')}
                        //         </p>
                        //     </div>
                        //     <div className='current-seats'>
                        //         <span>{getAmountOfSeats()}</span>
                        //         <span className='margin'></span>
                        //         <span className='button-text'
                        //             onClick={() => handleOpenManageSeatsModal()}>
                        //                 {t('billing.general.manage_seats_label')}
                        //         </span>
                        //     </div>
                        // </div>
                    )}
                    
                    {/* <div className='feature-comparison'>
                        <div className='feature-comparison-header'>
                            {showComparisonTable ? (
                                <span className='feature-comparison-label'
                                        onClick={() => setShowComparisonTable(prev => !prev)}>
                                    {t('billing.general.hide_feature_table_label')}
                                </span>
                            ) : (
                                <span className='feature-comparison-label'
                                        onClick={() => setShowComparisonTable(prev => !prev)}>
                                    {t('billing.general.show_feature_table_label')}
                                </span>
                            )}

                            <FontAwesomeIcon
                                icon={showComparisonTable ? faChevronUp : faChevronDown} 
                                className="feature-comparison-toggle-icon"
                            />                                  
                        </div>

                        {showComparisonTable && (
                            <div className={`comparison-table ${packages.filter(pkg => pkg.is_active && !pkg.is_view_only).length > 4 ? 'package-container' : 'container-filled'}`}>
                                
                                {Object.entries(featureComparison).map(([category, data]) => {
                                    // Calculate the maximum amount of rows for a main feature
                                    const maxRows = Math.max(...Object.values(data.features).map(features => features.length));

                                    return (
                                        <div key={category} 
                                                className="comparison-section">
                                            <div className="comparison-header">
                                                <h4>{t(`billing.feature_comparison.${category}.label`)}</h4>
                                            </div>

                                            <table className="comparison-section-table">
                                                <thead>
                                                    <tr>
                                                        {packages.filter(pkg => pkg.is_active && !pkg.is_view_only).map(pkg => (
                                                            <th key={pkg.key}>
                                                                {pkg.name}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {[...Array(maxRows)].map((_, rowIndex) => (
                                                        <tr key={rowIndex}>
                                                            {packages.filter(pkg => pkg.is_active && !pkg.is_view_only).map(pkg => {
                                                                const featureList = data.features[pkg.key as keyof typeof data.features] || [];
                                                                const feature = featureList[rowIndex];
                                                                
                                                                return (
                                                                    <td key={pkg.key}>
                                                                        {feature ? (
                                                                            <span className='tooltip-icon'>
                                                                                {t(`billing.feature_comparison.${category}.features.${feature}.label`)}
                                                                                <span className="tooltip tooltip-bottom">
                                                                                    {t(`billing.feature_comparison.${category}.features.${feature}.explanation`)}
                                                                                </span>
                                                                            </span>
                                                                        ) : "-"}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div> */}
                </div>
            )}
        </div>
    )
}

export default PackageSelection;